import { useEffect, useState } from 'react';
import { Scale, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import { WeightEntrySheet } from '@/components/WeightEntrySheet';

/**
 * Взвешивание — на главной, строкой, раз в неделю.
 *
 * ЗАЧЕМ (ревизия Профиля 05.09.2026). Вес мерил 21 человек, но за последний
 * месяц — двое. Ровно та же болезнь, что была у шагов: ввод жил только
 * в Профиле, куда заходят раз в месяц, и функция тихо умирала.
 *
 * ПОЧЕМУ НЕ КАЖДЫЙ ДЕНЬ, как шаги и вода. Ежедневное взвешивание для
 * худеющего — прямой путь к тревоге: вес скачет на воде, соли и фазе цикла,
 * и человек ловит эти скачки как провалы. Раз в неделю — интервал, на котором
 * видно тренд, а не шум. Поэтому строка появляется, только если последнего
 * замера нет семь дней, и исчезает, как только он сделан.
 *
 * По хартии ненавязчивости: строка, а не окно; «позже» убирает её на неделю.
 *
 * С 14.10.2026 строка — только напоминание: тап открывает то же окно ввода,
 * что плитка веса в Профиле (`WeightEntrySheet`). Постоянное место веса —
 * Профиль, там он доступен всегда, а не раз в неделю.
 */

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const SNOOZE_KEY = 'weight_snoozed_until';

export function WeightCard({ onSaved }: { onSaved?: () => void }) {
  const { profile } = useUserStore();
  const { haptic } = useTelegram();
  const [lastAt, setLastAt] = useState<string | null | undefined>(undefined);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!profile?.id) return;
    supabase
      .from('weight_log')
      .select('logged_at')
      .eq('user_id', profile.id)
      .order('logged_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => setLastAt(data?.logged_at ?? null));
  }, [profile?.id]);

  if (lastAt === undefined || !profile) return null;

  // Отложено человеком — молчим до срока
  try {
    const until = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    if (until > Date.now()) return null;
  } catch { /* localStorage может быть недоступен — тогда просто показываем */ }

  const daysSince = lastAt ? Math.floor((Date.now() - new Date(lastAt).getTime()) / 86400000) : null;
  // Взвешивался на этой неделе — предлагать нечего.
  if (daysSince !== null && daysSince < 7) return null;

  function snooze() {
    haptic('medium');
    try { localStorage.setItem(SNOOZE_KEY, String(Date.now() + WEEK_MS)); } catch {}
    setLastAt(new Date().toISOString());
  }

  // Окно — рядом с карточкой, а не внутри: у «стеклянной» карточки backdrop-filter,
  // и position: fixed внутри неё обрезается её границами (урок окна отправки партнёру).
  return (
    <>
    <div className="card px-3 py-2.5 mb-4" style={{ borderColor: 'rgba(61,216,255,0.25)' }}>
      <div className="flex items-center gap-2.5">
        <div className="w-[26px] h-[26px] rounded-full bg-sky-glow/15 flex items-center justify-center shrink-0">
          <Scale size={14} className="text-sky-glow" />
        </div>
        <button onClick={() => { setOpen(true); haptic('light'); }} className="flex-1 min-w-0 text-left">
          <div className="text-[13px] leading-snug">
            {daysSince === null
              ? 'Взвесишься? Первый замер — точка отсчёта'
              : `Прошла неделя с последнего взвешивания`}
          </div>
          <div className="text-[11px] text-ink-dim">
            Сейчас {profile.weight_kg} кг
            {profile.target_weight_kg ? ` · цель ${profile.target_weight_kg}` : ''}
            {' · вес всегда можно внести в Профиле'}
          </div>
        </button>
        <button onClick={snooze} aria-label="Позже" className="shrink-0 w-8 h-8 flex items-center justify-center text-ink-dim">
          <X size={14} />
        </button>
      </div>
    </div>

      <WeightEntrySheet
        open={open}
        onClose={() => setOpen(false)}
        onSaved={() => { setLastAt(new Date().toISOString()); onSaved?.(); }}
      />
    </>
  );
}
